/**
 * Computed statement grid: ✓/✗ per statement plus the
 * "Demonstrated in: Session 1, 3" traceability footnote (§1.5).
 *
 * When `onToggle` is provided each statement becomes a checkbox that overrides
 * the computed result (`overrides.statements` on PUT …/crf). A statement whose
 * effective value differs from the computed one shows a "Mentor override"
 * badge and a reset control that sends the computed value back.
 */
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { getLevelForm, PCC_C2_PROXY_MARKER_IDS, type IcfFormLevel } from '@/data/icfFormStatements';
import type { CrfComputed } from '@/types/mentorEngagements';
import { Chip, mentorStyles } from './shared';
import { checkboxA11y } from '@/utils/checkboxA11y';

interface CrfStatementGridProps {
  level: IcfFormLevel;
  computed: CrfComputed;
  /** Per-statement override; omit for a read-only grid. */
  onToggle?: (statementId: string, next: boolean) => void;
  /** Disables the toggles while a save is in flight. */
  busy?: boolean;
}

export function CrfStatementGrid({ level, computed, onToggle, busy = false }: CrfStatementGridProps) {
  const { t, language } = useTranslation();
  const form = getLevelForm(level);

  return (
    <View style={{ gap: 16 }} testID="crf_statement_grid">
      {onToggle ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.overrides.hint')}</Text> : null}
      {form.competencies.map((competency) => {
        const gate = computed.gates[competency.code];
        const gateLabel = gate?.met
          ? t('mentorEngagements.crf.gateMet', {
            code: competency.code,
            required: competency.required,
            total: competency.total,
            demonstrated: gate.demonstrated,
          })
          : t('mentorEngagements.crf.gateSummary', {
            code: competency.code,
            required: competency.required,
            total: competency.total,
            demonstrated: gate?.demonstrated ?? 0,
            short: gate?.short ?? competency.required,
          });

        return (
          <View
            key={competency.code}
            testID={`crf_competency_${competency.code}`}
            style={{ gap: 10, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}
          >
            <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
              <Text style={[mentorStyles.panelTitle, { fontSize: 15, flexShrink: 1 }]}>
                {competency.code} · {language === 'tr' ? competency.title_tr : competency.title_en}
              </Text>
              <Chip label={gateLabel} tone={gate?.met ? 'success' : 'warning'} />
            </View>

            {competency.statements.map((statement) => {
              const result = computed.statement_results[statement.id];
              const demonstrated = Boolean(result?.effective);
              // The server keeps `override: true` even when the override equals the
              // computed value (no delete semantic), so "overridden" is derived here.
              const overridden = Boolean(result) && result.effective !== result.computed;
              const naOnly = !demonstrated
                && (result?.sessions_na?.length ?? 0) > 0
                && (result?.sessions_demonstrated?.length ?? 0) === 0;

              const row = (
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 7 }}>
                  <MaterialIcons
                    name={demonstrated ? 'check-circle' : 'radio-button-unchecked'}
                    size={19}
                    color={demonstrated ? reviewDesk.success : reviewDesk.muted}
                  />
                  <View style={mentorStyles.flexOne}>
                    <Text style={mentorStyles.bodyText}>
                      <Text style={{ fontWeight: '800', color: reviewDesk.ink }}>{statement.id}  </Text>
                      {language === 'tr' ? statement.text_tr : statement.text_en}
                    </Text>
                    <View style={[mentorStyles.row, { marginTop: 3, gap: 8 }]}>
                      <Text style={mentorStyles.mutedText}>
                        {result?.sessions_demonstrated?.length
                          ? t('mentorEngagements.crf.demonstratedIn', {
                            sessions: result.sessions_demonstrated.join(', '),
                          })
                          : naOnly
                            ? t('mentorEngagements.crf.naEverywhere')
                            : t('mentorEngagements.crf.notDemonstrated')}
                      </Text>
                      {overridden ? (
                        <Chip label={t('mentorEngagements.overrides.badge')} tone="warning" icon="person" />
                      ) : null}
                    </View>
                  </View>
                </View>
              );

              if (!onToggle) {
                return <View key={statement.id} testID={`crf_statement_${statement.id}`}>{row}</View>;
              }
              // The reset control is a sibling of the toggle Pressable (not nested) so a
              // click on it cannot also fire the row toggle and race two PUTs.
              return (
                <View
                  key={statement.id}
                  style={overridden ? { backgroundColor: reviewDesk.amberSoft, borderRadius: 10, paddingHorizontal: 6 } : undefined}
                >
                  <Pressable
                    testID={`crf_override_${statement.id}`}
                    onPress={() => !busy && onToggle(statement.id, !demonstrated)}
                    disabled={busy}
                    {...checkboxA11y(demonstrated, busy)}
                    style={({ pressed }) => [pressed && mentorStyles.pressed, busy && { opacity: 0.7 }]}
                  >
                    <View testID={`crf_statement_${statement.id}`}>{row}</View>
                  </Pressable>
                  {overridden ? (
                    <Pressable
                      testID={`crf_override_reset_${statement.id}`}
                      onPress={() => !busy && onToggle(statement.id, Boolean(result.computed))}
                      disabled={busy}
                      accessibilityRole="button"
                      accessibilityState={{ disabled: busy }}
                      style={({ pressed }) => [{ alignSelf: 'flex-start', paddingLeft: 29, paddingBottom: 8 }, pressed && mentorStyles.pressed]}
                    >
                      <Text style={{ color: reviewDesk.accent, fontSize: 12, fontWeight: '700' }}>
                        {t('mentorEngagements.overrides.reset')}
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              );
            })}
          </View>
        );
      })}

      <Text style={mentorStyles.mutedText}>
        {level === 'pcc'
          ? t('mentorEngagements.crf.c2NotePcc', { markers: PCC_C2_PROXY_MARKER_IDS.join(', ') })
          : t('mentorEngagements.crf.c2NoteAccMcc')}
      </Text>
    </View>
  );
}

export default CrfStatementGrid;
